import { uploaderStyles } from './styles';
import type {
  FileRejection,
  FileUploaderApi,
  FileUploaderEventName,
  FileUploaderOptions,
  TiramAiFileUploaderGlobal,
  UploadAdapter,
  UploadContext,
  UploadFileRecord,
  UploadResult,
  UploadStatus,
} from './types';

export type {
  FileRejection,
  FileUploaderApi,
  FileUploaderClassNames,
  FileUploaderDensity,
  FileUploaderEventName,
  FileUploaderOptions,
  FileUploaderTheme,
  RejectionCode,
  TiramAiFileUploaderGlobal,
  UploadAdapter,
  UploadContext,
  UploadFileRecord,
  UploadResult,
  UploadStatus,
} from './types';

const ELEMENT_NAME = 'tiramai-file-uploader';
const API_KEY = Symbol('tiramai-file-uploader-api');
let idCounter = 0;

type InternalUploadTask = {
  controller: AbortController;
  xhr?: XMLHttpRequest;
  paused?: boolean;
};

type FileInputLike = File[] | FileList;

declare global {
  interface Window {
    TiramAiFileUploader: TiramAiFileUploaderGlobal;
  }
}

function hasDocument(): boolean {
  return typeof document !== 'undefined';
}

function nextId(): string {
  idCounter += 1;
  return `upload-${idCounter}`;
}

function normalizeOptions(options: FileUploaderOptions = {}): Required<Omit<FileUploaderOptions,
  'accept' | 'capture' | 'uploadUrl' | 'upload' | 'headers' | 'classNames' | 'themeOverrides' | 'renderFile' | 'renderThumbnail' | 'onFilesAdded' | 'onFileRejected' | 'onUploadComplete'
>> & Pick<FileUploaderOptions, 'accept' | 'capture' | 'uploadUrl' | 'upload' | 'headers' | 'classNames' | 'themeOverrides' | 'renderFile' | 'renderThumbnail' | 'onFilesAdded' | 'onFileRejected' | 'onUploadComplete'> {
  return {
    accept: options.accept,
    multiple: options.multiple ?? false,
    disabled: options.disabled ?? false,
    required: options.required ?? false,
    name: options.name ?? 'files',
    capture: options.capture,
    maxFiles: options.maxFiles ?? Infinity,
    maxSize: options.maxSize ?? (options.maxSizeMB ? options.maxSizeMB * 1024 * 1024 : Infinity),
    maxSizeMB: options.maxSizeMB ?? Infinity,
    maxTotalSize: options.maxTotalSize ?? Infinity,
    allowPaste: options.allowPaste ?? false,
    pasteScope: options.pasteScope ?? 'focused',
    allowFolderDrop: options.allowFolderDrop ?? false,
    dedupe: options.dedupe ?? true,
    autoUpload: options.autoUpload ?? false,
    uploadUrl: options.uploadUrl,
    upload: options.upload,
    chunkSize: options.chunkSize ?? 0,
    parallelUploads: options.parallelUploads ?? 2,
    headers: options.headers,
    fieldName: options.fieldName ?? 'file',
    withCredentials: options.withCredentials ?? false,
    showThumbnails: options.showThumbnails ?? true,
    thumbnailMaxSize: options.thumbnailMaxSize ?? 8 * 1024 * 1024,
    clearOnSuccess: options.clearOnSuccess ?? false,
    theme: options.theme ?? 'system',
    density: options.density ?? 'comfortable',
    highContrast: options.highContrast ?? false,
    rtl: options.rtl ?? false,
    shadowDom: options.shadowDom ?? true,
    unstyled: options.unstyled ?? false,
    classNames: options.classNames,
    themeOverrides: options.themeOverrides,
    label: options.label ?? 'Drag files here or browse files',
    hint: options.hint ?? 'Drop files here, paste files, or use the native file picker.',
    browseLabel: options.browseLabel ?? 'Browse files',
    uploadLabel: options.uploadLabel ?? 'Upload all',
    emptyLabel: options.emptyLabel ?? 'No files selected yet.',
    renderFile: options.renderFile,
    renderThumbnail: options.renderThumbnail,
    onFilesAdded: options.onFilesAdded,
    onFileRejected: options.onFileRejected,
    onUploadComplete: options.onUploadComplete,
  };
}

function parseBoolean(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  if (value === 'false') return false;
  return true;
}

function parseNumber(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function fileSignature(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept || accept.trim() === '') return true;
  const rules = accept.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
  if (rules.length === 0) return true;
  const type = file.type.toLowerCase();
  const extension = `.${getExtension(file.name)}`;

  return rules.some((rule) => {
    if (rule.startsWith('.')) return extension === rule;
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

function statusLabel(status: UploadStatus): string {
  const labels: Record<UploadStatus, string> = {
    queued: 'Queued',
    validating: 'Validating',
    ready: 'Ready',
    uploading: 'Uploading',
    paused: 'Paused',
    success: 'Uploaded',
    error: 'Error',
    canceled: 'Canceled',
  };
  return labels[status];
}

function isUploadable(status: UploadStatus): boolean {
  return status === 'queued' || status === 'ready' || status === 'error' || status === 'canceled';
}

export class TiramAiFileUploaderElement extends HTMLElement {
  static observedAttributes = [
    'accept',
    'multiple',
    'disabled',
    'required',
    'name',
    'capture',
    'max-files',
    'max-size',
    'max-size-mb',
    'max-total-size',
    'allow-paste',
    'allow-folder-drop',
    'auto-upload',
    'upload-url',
    'show-thumbnails',
    'theme',
    'density',
    'label',
    'hint',
  ];

  private options = normalizeOptions();
  private rootNode: ShadowRoot | HTMLElement = this;
  private input?: HTMLInputElement;
  private dropzone?: HTMLElement;
  private list?: HTMLOListElement;
  private summary?: HTMLElement;
  private liveRegion?: HTMLElement;
  private files: UploadFileRecord[] = [];
  private uploadTasks = new Map<string, InternalUploadTask>();
  private initialized = false;
  private draggingDepth = 0;
  private focused = false;
  private documentPasteHandler = (event: ClipboardEvent) => this.handlePaste(event);

  connectedCallback(): void {
    if (this.initialized) return;
    this.options = normalizeOptions({ ...this.options, ...this.optionsFromAttributes() });
    this.initialized = true;
    this.rootNode = this.options.shadowDom ? this.attachShadow({ mode: 'open' }) : this;
    this.setAttribute('role', 'group');
    this.setAttribute('aria-label', 'File uploader');
    this.applyHostState();
    this.renderShell();
    this.bindEvents();
    this.render();
  }

  disconnectedCallback(): void {
    document.removeEventListener('paste', this.documentPasteHandler);
    this.uploadTasks.forEach((task) => task.controller.abort());
    this.revokeAllPreviews();
  }

  attributeChangedCallback(): void {
    if (!this.initialized) return;
    this.updateOptions(this.optionsFromAttributes());
  }

  browse(): void {
    if (this.options.disabled) return;
    this.input?.click();
  }

  async addFiles(filesLike: FileInputLike, relativePaths = new Map<File, string>()): Promise<UploadFileRecord[]> {
    if (this.options.disabled) return [];
    const incoming = Array.from(filesLike);
    const accepted: UploadFileRecord[] = [];
    const signatures = new Set(this.files.map((record) => fileSignature(record.file)));
    let acceptedCount = this.files.length;
    let totalSize = this.files.reduce((sum, record) => sum + record.size, 0);

    for (const file of incoming) {
      const duplicate = signatures.has(fileSignature(file));
      const rejection = this.validateFile(file, acceptedCount, totalSize, duplicate);
      if (rejection) {
        this.rejectFile(rejection);
        continue;
      }

      signatures.add(fileSignature(file));
      acceptedCount += 1;
      totalSize += file.size;
      const record = this.createRecord(file, relativePaths.get(file));
      accepted.push(record);
      this.files.push(record);
      this.emit('file-accepted', { file: record });
    }

    if (accepted.length > 0) {
      this.emit('files-added', { files: accepted });
      this.options.onFilesAdded?.(accepted);
      this.announce(`${accepted.length} file${accepted.length === 1 ? '' : 's'} added.`);
      this.render();
      if (this.options.autoUpload) {
        await this.uploadAll();
      }
    } else {
      this.render();
    }

    this.emit('change', { files: this.getFiles() });
    return accepted;
  }

  async uploadAll(): Promise<void> {
    const queue = this.files.filter((file) => isUploadable(file.status));
    const parallel = Math.max(1, Math.floor(this.options.parallelUploads));
    let index = 0;

    const workers = Array.from({ length: Math.min(parallel, queue.length) }, async () => {
      while (index < queue.length) {
        const next = queue[index];
        index += 1;
        await this.uploadFile(next.id);
      }
    });

    await Promise.all(workers);
    this.emit('upload-complete', { files: this.getFiles() });
    this.options.onUploadComplete?.(this.getFiles());
    this.announce('Upload queue complete.');

    if (this.options.clearOnSuccess && this.files.every((file) => file.status === 'success')) {
      this.clear();
    }
  }

  async uploadFile(id: string): Promise<void> {
    const record = this.files.find((file) => file.id === id);
    if (!record || record.status === 'uploading' || this.options.disabled) return;
    if (!this.options.upload && !this.options.uploadUrl) {
      this.setFileError(record, 'No upload adapter or uploadUrl configured.');
      return;
    }

    const controller = new AbortController();
    const task: InternalUploadTask = { controller };
    this.uploadTasks.set(record.id, task);
    record.status = 'uploading';
    record.progress = Math.max(0, record.progress || 0);
    record.error = undefined;
    this.emit('upload-start', { file: record });
    this.render();

    const context: UploadContext = {
      signal: controller.signal,
      setProgress: (progress) => this.setProgress(record, progress),
      setStatus: (status) => this.setStatus(record, status),
      setError: (message) => this.setFileError(record, message),
      emit: (name, detail) => this.emit(name, detail),
    };

    try {
      let result: UploadResult;
      if (this.options.upload) {
        const adapter = this.options.upload;
        result = typeof adapter === 'function'
          ? await adapter(record, context)
          : await adapter.upload(record, context);
      } else if (this.options.chunkSize > 0) {
        result = await this.uploadInChunks(record, context, task);
      } else {
        result = await this.uploadViaXhr(record, context, task);
      }

      record.status = 'success';
      record.progress = 1;
      record.result = result;
      this.uploadTasks.delete(record.id);
      this.emit('upload-success', { file: record, result });
      this.announce(`${record.name} uploaded.`);
    } catch (error) {
      this.uploadTasks.delete(record.id);
      if (controller.signal.aborted) {
        record.status = 'canceled';
        record.error = undefined;
        this.emit('upload-cancel', { file: record });
      } else {
        this.setFileError(record, error instanceof Error ? error.message : 'Upload failed.');
        this.emit('upload-error', { file: record, error });
      }
    } finally {
      this.render();
      this.emit('change', { files: this.getFiles() });
    }
  }

  async pause(id: string): Promise<void> {
    const record = this.files.find((file) => file.id === id);
    const task = this.uploadTasks.get(id);
    if (!record || !task || record.status !== 'uploading') return;
    task.paused = true;
    record.status = 'paused';
    if (typeof this.options.upload === 'object') {
      await this.options.upload.pause?.(record);
    }
    task.xhr?.abort();
    task.controller.abort();
    this.render();
  }

  async resume(id: string): Promise<void> {
    const record = this.files.find((file) => file.id === id);
    if (!record || record.status !== 'paused') return;
    if (typeof this.options.upload === 'object') {
      await this.options.upload.resume?.(record);
    }
    await this.uploadFile(id);
  }

  async cancel(id: string): Promise<void> {
    const record = this.files.find((file) => file.id === id);
    if (!record) return;
    const task = this.uploadTasks.get(id);
    task?.xhr?.abort();
    task?.controller.abort();
    if (typeof this.options.upload === 'object') {
      await this.options.upload.cancel?.(record);
    }
    record.status = 'canceled';
    this.uploadTasks.delete(id);
    this.emit('upload-cancel', { file: record });
    this.render();
  }

  removeFile(id: string): void {
    const index = this.files.findIndex((file) => file.id === id);
    if (index < 0) return;
    const [record] = this.files.splice(index, 1);
    this.cancel(id).catch(() => undefined);
    if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
    this.emit('file-removed', { file: record });
    this.emit('change', { files: this.getFiles() });
    this.announce(`${record.name} removed.`);
    this.render();
  }

  clear(): void {
    this.uploadTasks.forEach((task) => {
      task.xhr?.abort();
      task.controller.abort();
    });
    this.uploadTasks.clear();
    this.revokeAllPreviews();
    this.files = [];
    if (this.input) this.input.value = '';
    this.emit('queue-clear', {});
    this.emit('change', { files: [] });
    this.announce('File queue cleared.');
    this.render();
  }

  getFiles(): UploadFileRecord[] {
    return this.files.map((file) => ({ ...file }));
  }

  updateOptions(options: Partial<FileUploaderOptions>): void {
    this.options = normalizeOptions({ ...this.options, ...options });
    this.applyHostState();
    if (this.input) this.syncInputAttributes();
    this.applyThemeOverrides();
    this.render();
  }

  destroy(): void {
    this.clear();
    super.remove();
  }

  private optionsFromAttributes(): Partial<FileUploaderOptions> {
    return {
      accept: this.getAttribute('accept') ?? undefined,
      multiple: parseBoolean(this.getAttribute('multiple')),
      disabled: parseBoolean(this.getAttribute('disabled')),
      required: parseBoolean(this.getAttribute('required')),
      name: this.getAttribute('name') ?? undefined,
      capture: this.getAttribute('capture') ?? undefined,
      maxFiles: parseNumber(this.getAttribute('max-files')),
      maxSize: parseNumber(this.getAttribute('max-size')),
      maxSizeMB: parseNumber(this.getAttribute('max-size-mb')),
      maxTotalSize: parseNumber(this.getAttribute('max-total-size')),
      allowPaste: parseBoolean(this.getAttribute('allow-paste')),
      allowFolderDrop: parseBoolean(this.getAttribute('allow-folder-drop')),
      autoUpload: parseBoolean(this.getAttribute('auto-upload')),
      uploadUrl: this.getAttribute('upload-url') ?? undefined,
      showThumbnails: parseBoolean(this.getAttribute('show-thumbnails')),
      theme: this.getAttribute('theme') ?? undefined,
      density: this.getAttribute('density') as FileUploaderOptions['density'] | undefined,
      label: this.getAttribute('label') ?? undefined,
      hint: this.getAttribute('hint') ?? undefined,
    };
  }

  private applyHostState(): void {
    this.dataset.theme = this.options.theme;
    this.dataset.density = this.options.density;
    this.dataset.disabled = String(this.options.disabled);
    this.dir = this.options.rtl ? 'rtl' : 'ltr';
    this.applyThemeOverrides();
  }

  private applyThemeOverrides(): void {
    Object.entries(this.options.themeOverrides ?? {}).forEach(([key, value]) => {
      this.style.setProperty(key, String(value));
    });
  }

  private renderShell(): void {
    this.rootNode.innerHTML = `
      ${this.options.unstyled ? '' : `<style>${uploaderStyles}</style>`}
      <div class="tiram-uploader" part="root">
        <div class="tiram-uploader-sr" aria-live="polite" aria-atomic="true" data-live></div>
        <input class="tiram-uploader-input" data-input type="file" />
        <div class="tiram-uploader-dropzone" data-dropzone role="button" tabindex="0" aria-label="File upload dropzone. Activate to browse files or drag and drop files here.">
          <div>
            <div class="tiram-uploader-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M12 15V3m0 0 4 4m-4-4L8 7M4 15v3a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
            <slot name="label"><p class="tiram-uploader-label">${this.escape(this.options.label).replace('browse files', '<span>browse files</span>')}</p></slot>
            <slot name="hint"><p class="tiram-uploader-hint">${this.escape(this.options.hint)}</p></slot>
          </div>
        </div>
        <div class="tiram-uploader-toolbar" data-toolbar>
          <div class="tiram-uploader-summary" data-summary></div>
          <div class="tiram-uploader-actions">
            <slot name="actions"></slot>
            <button class="tiram-uploader-button" data-browse type="button" aria-label="Browse files">${this.escape(this.options.browseLabel)}</button>
            <button class="tiram-uploader-button" data-upload data-variant="primary" type="button" aria-label="Upload all files">${this.escape(this.options.uploadLabel)}</button>
            <button class="tiram-uploader-button" data-clear type="button" aria-label="Clear file queue">Clear</button>
          </div>
        </div>
        <slot name="empty"><div class="tiram-uploader-empty" data-empty>${this.escape(this.options.emptyLabel)}</div></slot>
        <ol class="tiram-uploader-list" data-list aria-label="Selected files"></ol>
      </div>
    `;
    this.input = this.rootNode.querySelector<HTMLInputElement>('[data-input]') ?? undefined;
    this.dropzone = this.rootNode.querySelector<HTMLElement>('[data-dropzone]') ?? undefined;
    this.list = this.rootNode.querySelector<HTMLOListElement>('[data-list]') ?? undefined;
    this.summary = this.rootNode.querySelector<HTMLElement>('[data-summary]') ?? undefined;
    this.liveRegion = this.rootNode.querySelector<HTMLElement>('[data-live]') ?? undefined;
    this.syncInputAttributes();
  }

  private bindEvents(): void {
    this.input?.addEventListener('change', () => {
      if (this.input?.files) {
        this.addFiles(this.input.files).catch(() => undefined);
      }
    });

    this.dropzone?.addEventListener('click', () => this.browse());
    this.dropzone?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.browse();
      }
    });
    this.dropzone?.addEventListener('focusin', () => { this.focused = true; });
    this.dropzone?.addEventListener('focusout', () => { this.focused = false; });
    this.dropzone?.addEventListener('dragenter', (event) => this.handleDrag(event, true));
    this.dropzone?.addEventListener('dragover', (event) => this.handleDrag(event, true));
    this.dropzone?.addEventListener('dragleave', (event) => this.handleDragLeave(event));
    this.dropzone?.addEventListener('drop', (event) => this.handleDrop(event));
    this.dropzone?.addEventListener('paste', (event) => this.handlePaste(event));

    this.rootNode.querySelector<HTMLElement>('[data-browse]')?.addEventListener('click', () => this.browse());
    this.rootNode.querySelector<HTMLElement>('[data-upload]')?.addEventListener('click', () => this.uploadAll());
    this.rootNode.querySelector<HTMLElement>('[data-clear]')?.addEventListener('click', () => this.clear());

    document.removeEventListener('paste', this.documentPasteHandler);
    if (this.options.allowPaste && this.options.pasteScope === 'document') {
      document.addEventListener('paste', this.documentPasteHandler);
    }
  }

  private syncInputAttributes(): void {
    if (!this.input) return;
    this.input.accept = this.options.accept ?? '';
    this.input.multiple = this.options.multiple;
    this.input.disabled = this.options.disabled;
    this.input.required = this.options.required;
    this.input.name = this.options.name;
    if (this.options.capture) this.input.setAttribute('capture', this.options.capture);
    else this.input.removeAttribute('capture');
  }

  private render(): void {
    if (!this.list || !this.summary) return;
    const empty = this.rootNode.querySelector<HTMLElement>('[data-empty]');
    const uploadButton = this.rootNode.querySelector<HTMLButtonElement>('[data-upload]');
    const clearButton = this.rootNode.querySelector<HTMLButtonElement>('[data-clear]');
    const browseButton = this.rootNode.querySelector<HTMLButtonElement>('[data-browse]');

    if (this.dropzone) {
      this.dropzone.dataset.disabled = String(this.options.disabled);
      this.dropzone.setAttribute('aria-disabled', String(this.options.disabled));
    }

    browseButton && (browseButton.disabled = this.options.disabled);
    uploadButton && (uploadButton.disabled = this.options.disabled || !this.files.some((file) => isUploadable(file.status)));
    clearButton && (clearButton.disabled = this.files.length === 0);

    const total = this.files.reduce((sum, file) => sum + file.size, 0);
    const uploaded = this.files.filter((file) => file.status === 'success').length;
    this.summary.textContent = this.files.length === 0
      ? 'No files selected'
      : `${this.files.length} file${this.files.length === 1 ? '' : 's'} • ${formatBytes(total)} • ${uploaded} uploaded`;

    if (empty) empty.hidden = this.files.length > 0;
    this.list.replaceChildren(...this.files.map((file) => this.renderFile(file)));
  }

  private renderFile(record: UploadFileRecord): HTMLElement {
    if (this.options.renderFile) return this.options.renderFile(record, this);

    const li = document.createElement('li');
    li.className = `tiram-uploader-file ${this.options.classNames?.file ?? ''}`.trim();
    li.dataset.fileId = record.id;
    li.dataset.status = record.status;
    li.setAttribute('aria-label', `${record.name}, ${formatBytes(record.size)}, ${statusLabel(record.status)}`);

    const thumbnail = this.renderThumbnail(record);
    const main = document.createElement('div');
    main.className = 'tiram-uploader-file-main';

    const name = document.createElement('p');
    name.className = 'tiram-uploader-file-name';
    name.textContent = record.relativePath || record.name;
    const status = document.createElement('span');
    status.className = 'tiram-uploader-file-status';
    status.dataset.status = record.status;
    status.textContent = statusLabel(record.status);
    name.appendChild(status);
    main.appendChild(name);

    const meta = document.createElement('p');
    meta.className = 'tiram-uploader-file-meta';
    meta.textContent = `${record.extension ? record.extension.toUpperCase() : 'FILE'} • ${formatBytes(record.size)}`;
    main.appendChild(meta);

    if (record.status === 'uploading' || record.status === 'paused' || record.status === 'success') {
      const progress = document.createElement('div');
      progress.className = 'tiram-uploader-progress';
      progress.setAttribute('role', 'progressbar');
      progress.setAttribute('aria-valuemin', '0');
      progress.setAttribute('aria-valuemax', '100');
      progress.setAttribute('aria-valuenow', String(Math.round(record.progress * 100)));
      const bar = document.createElement('div');
      bar.className = 'tiram-uploader-progress-bar';
      bar.style.setProperty('--progress', String(Math.max(0, Math.min(1, record.progress))));
      progress.appendChild(bar);
      main.appendChild(progress);
    }

    if (record.error) {
      const error = document.createElement('p');
      error.className = 'tiram-uploader-file-error';
      error.textContent = record.error;
      main.appendChild(error);
    }

    const actions = document.createElement('div');
    actions.className = 'tiram-uploader-file-actions';
    if (record.status === 'uploading') {
      const cancelBtn = this.fileButton('Cancel', () => this.cancel(record.id), 'cancel');
      cancelBtn.setAttribute('aria-label', `Cancel upload for ${record.name}`);
      actions.appendChild(cancelBtn);
    } else if (record.status === 'paused') {
      const resumeBtn = this.fileButton('Resume', () => this.resume(record.id), 'resume');
      resumeBtn.setAttribute('aria-label', `Resume upload for ${record.name}`);
      const removeBtn = this.fileButton('Remove', () => this.removeFile(record.id), 'remove');
      removeBtn.setAttribute('aria-label', `Remove ${record.name}`);
      actions.append(resumeBtn, removeBtn);
    } else if (isUploadable(record.status)) {
      const uploadBtn = this.fileButton('Upload', () => this.uploadFile(record.id), 'upload');
      uploadBtn.setAttribute('aria-label', `Upload ${record.name}`);
      const removeBtn = this.fileButton('Remove', () => this.removeFile(record.id), 'remove');
      removeBtn.setAttribute('aria-label', `Remove ${record.name}`);
      actions.append(uploadBtn, removeBtn);
    } else {
      const removeBtn = this.fileButton('Remove', () => this.removeFile(record.id), 'remove');
      removeBtn.setAttribute('aria-label', `Remove ${record.name}`);
      actions.appendChild(removeBtn);
    }

    li.append(thumbnail, main, actions);
    return li;
  }

  private renderThumbnail(record: UploadFileRecord): HTMLElement {
    if (this.options.renderThumbnail) return this.options.renderThumbnail(record);
    const thumbnail = document.createElement('div');
    thumbnail.className = `tiram-uploader-file-thumbnail ${this.options.classNames?.thumbnail ?? ''}`.trim();
    thumbnail.setAttribute('aria-hidden', 'true');
    if (record.previewUrl) {
      const img = document.createElement('img');
      img.src = record.previewUrl;
      img.alt = `Preview of ${record.name}`;
      img.loading = 'lazy';
      thumbnail.appendChild(img);
    } else {
      thumbnail.textContent = record.extension || 'file';
    }
    return thumbnail;
  }

  private fileButton(label: string, onClick: () => void | Promise<void>, action?: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tiram-uploader-file-button';
    button.textContent = label;
    if (action) button.dataset.action = action;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      void onClick();
    });
    return button;
  }

  private createRecord(file: File, relativePath?: string): UploadFileRecord {
    const extension = getExtension(file.name);
    const record: UploadFileRecord = {
      id: nextId(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      extension,
      status: 'ready',
      progress: 0,
      relativePath: relativePath || (file as File & { webkitRelativePath?: string }).webkitRelativePath || undefined,
    };

    if (this.options.showThumbnails && file.type.startsWith('image/') && file.size <= this.options.thumbnailMaxSize) {
      record.previewUrl = URL.createObjectURL(file);
    }

    return record;
  }

  private validateFile(file: File, acceptedCount: number, totalSize: number, duplicate: boolean): FileRejection | null {
    if (!this.options.multiple && acceptedCount >= 1) {
      return { file, code: 'max-files', message: 'Only one file is allowed.' };
    }
    if (acceptedCount >= this.options.maxFiles) {
      return { file, code: 'max-files', message: `Maximum ${this.options.maxFiles} files allowed.` };
    }
    if (this.options.dedupe && duplicate) {
      return { file, code: 'duplicate', message: 'This file is already selected.' };
    }
    if (!matchesAccept(file, this.options.accept)) {
      return { file, code: 'accept', message: `File type is not allowed. Expected ${this.options.accept}.` };
    }
    if (file.size > this.options.maxSize) {
      return { file, code: 'max-size', message: `File is too large. Maximum size is ${formatBytes(this.options.maxSize)}.` };
    }
    if (totalSize + file.size > this.options.maxTotalSize) {
      return { file, code: 'max-total-size', message: `Total upload size cannot exceed ${formatBytes(this.options.maxTotalSize)}.` };
    }
    return null;
  }

  private rejectFile(rejection: FileRejection): void {
    this.emit('file-rejected', { rejection });
    this.options.onFileRejected?.(rejection);
    this.announce(`${rejection.file.name} rejected. ${rejection.message}`);
  }

  private handleDrag(event: DragEvent, active: boolean): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.options.disabled) return;
    this.draggingDepth += active ? 1 : 0;
    if (this.dropzone) this.dropzone.dataset.dragging = 'true';
  }

  private handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingDepth = Math.max(0, this.draggingDepth - 1);
    if (this.draggingDepth === 0 && this.dropzone) this.dropzone.dataset.dragging = 'false';
  }

  private async handleDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.draggingDepth = 0;
    if (this.dropzone) this.dropzone.dataset.dragging = 'false';
    if (this.options.disabled || !event.dataTransfer) return;

    if (this.options.allowFolderDrop && event.dataTransfer.items?.length) {
      const { files, paths } = await this.collectDataTransferFiles(event.dataTransfer);
      await this.addFiles(files, paths);
      return;
    }

    await this.addFiles(event.dataTransfer.files);
  }

  private async handlePaste(event: ClipboardEvent): Promise<void> {
    if (!this.options.allowPaste || this.options.disabled) return;
    if (this.options.pasteScope === 'focused' && !this.focused && !this.matches(':focus-within')) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length === 0) return;
    event.preventDefault();
    await this.addFiles(files);
  }

  private async collectDataTransferFiles(dataTransfer: DataTransfer): Promise<{ files: File[]; paths: Map<File, string> }> {
    const files: File[] = [];
    const paths = new Map<File, string>();
    const items = Array.from(dataTransfer.items ?? []);

    for (const item of items) {
      const entry = (item as DataTransferItem & { webkitGetAsEntry?: () => unknown }).webkitGetAsEntry?.();
      if (entry) {
        await this.walkEntry(entry, '', files, paths);
      } else {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length === 0) {
      files.push(...Array.from(dataTransfer.files));
    }

    return { files, paths };
  }

  private async walkEntry(entry: unknown, prefix: string, files: File[], paths: Map<File, string>): Promise<void> {
    const e = entry as {
      isFile?: boolean;
      isDirectory?: boolean;
      name?: string;
      file?: (success: (file: File) => void, error: (error: DOMException) => void) => void;
      createReader?: () => { readEntries: (success: (entries: unknown[]) => void, error: (error: DOMException) => void) => void };
    };

    if (e.isFile && e.file) {
      const file = await new Promise<File>((resolve, reject) => e.file?.(resolve, reject));
      files.push(file);
      paths.set(file, `${prefix}${file.name}`);
      return;
    }

    if (e.isDirectory && e.createReader) {
      const reader = e.createReader();
      const entries = await new Promise<unknown[]>((resolve, reject) => reader.readEntries(resolve, reject));
      const dirPrefix = `${prefix}${e.name ?? 'folder'}/`;
      await Promise.all(entries.map((child) => this.walkEntry(child, dirPrefix, files, paths)));
    }
  }

  private async uploadViaXhr(record: UploadFileRecord, context: UploadContext, task: InternalUploadTask): Promise<UploadResult> {
    if (!this.options.uploadUrl) throw new Error('uploadUrl is required.');
    const form = new FormData();
    form.append(this.options.fieldName, record.file, record.name);
    if (record.relativePath) form.append('relativePath', record.relativePath);
    return this.sendXhr(this.options.uploadUrl, form, context, task);
  }

  private async uploadInChunks(record: UploadFileRecord, context: UploadContext, task: InternalUploadTask): Promise<UploadResult> {
    if (!this.options.uploadUrl) throw new Error('uploadUrl is required for chunked uploads.');
    const chunkSize = Math.max(1, this.options.chunkSize);
    const chunks = Math.ceil(record.size / chunkSize);
    let response: unknown;

    for (let index = 0; index < chunks; index += 1) {
      if (context.signal.aborted) throw new DOMException('Upload canceled.', 'AbortError');
      const start = index * chunkSize;
      const end = Math.min(record.size, start + chunkSize);
      const chunk = record.file.slice(start, end);
      const form = new FormData();
      form.append(this.options.fieldName, chunk, record.name);
      form.append('fileName', record.name);
      form.append('chunkIndex', String(index));
      form.append('chunkCount', String(chunks));
      form.append('uploadId', record.id);
      if (record.relativePath) form.append('relativePath', record.relativePath);
      response = (await this.sendXhr(this.options.uploadUrl, form, context, task, {
        'Content-Range': `bytes ${start}-${end - 1}/${record.size}`,
        'X-Upload-Id': record.id,
        'X-Chunk-Index': String(index),
        'X-Chunk-Count': String(chunks),
      })).response;
      record.uploadedBytes = end;
      context.setProgress(end / record.size);
    }

    return { response, metadata: { chunks } };
  }

  private sendXhr(url: string, body: FormData, context: UploadContext, task: InternalUploadTask, extraHeaders: Record<string, string> = {}): Promise<UploadResult> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      task.xhr = xhr;
      xhr.open('POST', url, true);
      xhr.withCredentials = this.options.withCredentials;
      Object.entries({ ...(this.options.headers ?? {}), ...extraHeaders }).forEach(([key, value]) => xhr.setRequestHeader(key, value));

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;
        context.setProgress(event.loaded / event.total);
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let response: unknown = xhr.responseText;
          try { response = JSON.parse(xhr.responseText); } catch { /* keep text */ }
          resolve({ response });
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}.`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network upload failed.')));
      xhr.addEventListener('abort', () => reject(new DOMException('Upload canceled.', 'AbortError')));
      context.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(body);
    });
  }

  private setProgress(record: UploadFileRecord, progress: number): void {
    record.progress = Math.max(0, Math.min(1, progress));
    this.emit('upload-progress', { file: record, progress: record.progress });
    this.render();
  }

  private setStatus(record: UploadFileRecord, status: UploadStatus): void {
    record.status = status;
    this.render();
  }

  private setFileError(record: UploadFileRecord, message: string): void {
    record.status = 'error';
    record.error = message;
    this.announce(`${record.name}: ${message}`);
    this.render();
  }

  private emit(name: FileUploaderEventName, detail: unknown = {}): void {
    this.dispatchEvent(new CustomEvent(`tiramai-uploader:${name}`, {
      bubbles: true,
      composed: true,
      detail,
    }));
  }

  private announce(message: string): void {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      if (this.liveRegion) this.liveRegion.textContent = message;
    }, 20);
  }

  private revokeAllPreviews(): void {
    this.files.forEach((record) => {
      if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
    });
  }

  private escape(value: string): string {
    return value.replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[char] ?? char));
  }
}

const apiCache = new WeakMap<TiramAiFileUploaderElement, FileUploaderApi>();

function apiFor(element: TiramAiFileUploaderElement): FileUploaderApi {
  const existing = apiCache.get(element);
  if (existing) return existing;
  const api: FileUploaderApi = {
    browse: () => element.browse(),
    addFiles: (files, paths) => element.addFiles(files, paths),
    uploadAll: () => element.uploadAll(),
    uploadFile: (id) => element.uploadFile(id),
    pause: (id) => element.pause(id),
    resume: (id) => element.resume(id),
    cancel: (id) => element.cancel(id),
    remove: (id) => element.removeFile(id),
    clear: () => element.clear(),
    getFiles: () => element.getFiles(),
    updateOptions: (options) => element.updateOptions(options),
    destroy: () => element.destroy(),
  };
  apiCache.set(element, api);
  return api;
}

function define(): void {
  if (!hasDocument()) return;
  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, TiramAiFileUploaderElement);
  }
}

function resolveTarget(target: string | HTMLElement): HTMLElement {
  if (typeof target === 'string') {
    const found = document.querySelector<HTMLElement>(target);
    if (!found) throw new Error(`TiramAiFileUploader target not found: ${target}`);
    return found;
  }
  return target;
}

function create(target: string | HTMLElement, options: FileUploaderOptions = {}): FileUploaderApi {
  if (!hasDocument()) throw new Error('TiramAiFileUploader requires a browser document.');
  define();
  const element = resolveTarget(target);
  let uploader: TiramAiFileUploaderElement;

  if (element instanceof TiramAiFileUploaderElement) {
    uploader = element;
  } else if (element.tagName.toLowerCase() === ELEMENT_NAME) {
    uploader = element as TiramAiFileUploaderElement;
  } else {
    uploader = document.createElement(ELEMENT_NAME) as TiramAiFileUploaderElement;
    element.appendChild(uploader);
  }

  uploader.updateOptions(options);
  const api = apiFor(uploader);
  (uploader as unknown as Record<symbol, FileUploaderApi>)[API_KEY] = api;
  return api;
}

function get(target: string | HTMLElement): FileUploaderApi | null {
  if (!hasDocument()) return null;
  const element = resolveTarget(target);
  const uploader = element instanceof TiramAiFileUploaderElement
    ? element
    : element.querySelector<TiramAiFileUploaderElement>(ELEMENT_NAME);
  return uploader ? apiFor(uploader) : null;
}

export const TiramAiFileUploader: TiramAiFileUploaderGlobal = { define, create, get };

if (hasDocument()) {
  define();
  window.TiramAiFileUploader = TiramAiFileUploader;
}

export default TiramAiFileUploader;
